<script setup lang="ts">
import type { CreateQuickSale, FilePath } from '~/types'

const props = defineProps<{ payload: CreateQuickSale }>()
const emit = defineEmits<{ (e: 'save', payload: CreateQuickSale & { language: string }): void }>()

interface PayloadData extends Omit<CreateQuickSale, 'templateFile'> {
  // Since FormKitFileDropzone returns an array of files
  templateFile?: FilePath[]
}

const { cloned } = useCloned<PayloadData>({
  ...props.payload,
  templateFile: props.payload.templateFile ? [props.payload.templateFile] : undefined,
})

const hasCustomTemplate = ref<boolean>(false)
const language = ref<string>('de')

function handleSubmit(data: PayloadData) {
  const payload: CreateQuickSale & { language: string } = {
    ...data,
    templateFile: hasCustomTemplate.value && data.templateFile && data.templateFile.length > 0 ? data.templateFile[0] : undefined,
    language: language.value,
  }
  emit('save', payload)
}

const { fetch: openPreview, isLoading: isLoadingPreview } = useOpenDocumentInNewTab('/api/pdf/quickSale/preview', () => ({
  ...cloned.value,
  templateFile: cloned.value.templateFile && cloned.value.templateFile.length > 0 ? cloned.value.templateFile[0] : undefined,
  language: language.value,
}))
</script>

<template>
  <!-- @vue-expect-error: After upgrading to latest Vue 3.5 and TS 5.8 in #4136 inference became stricter, generating a new error for a working form, that had no explicit error before. To enable the upgrade we add this ignore: The TS-error was there before, it was just not caught. Now, its at least explicit and can be fixed in the future. TODO: Fix, improve or replace once we agree on a systematic fix -->
  <FormKit v-model="cloned" :actions="false" type="form">
    <FormKit
      name="price"
      type="number"
      number="float"
      step="0.01"
      :label="$t('quickSale.creationForm.price.label')"
    />
    <FormKit
      name="description"
      type="text"
      :label="$t('quickSale.creationForm.description.label')"
      validation="required"
    />
    <FormKit
      name="additionalInformation"
      type="textarea"
      :label="$t('quickSale.creationForm.additionalInformation.label')"
    />
    <FormKit
      v-model="hasCustomTemplate"
      type="checkbox"
      :label="$t('quickSale.creationForm.customTemplate.label')"
    />
    <div
      v-if="hasCustomTemplate"
      class="text-sm space-y-2"
    >
      <p class="font-bold">
        {{ $t('quickSale.creationForm.templateNotes.title') }}
      </p>
      <ul class="list-disc list-inside space-y-1">
        <li>{{ $t('quickSale.creationForm.templateNotes.item1') }}</li>
        <li>{{ $t('quickSale.creationForm.templateNotes.item2') }}</li>
        <li>
          {{ $t('quickSale.creationForm.templateNotes.item3') }}
          <ul class="list-disc list-inside ml-4 space-y-1">
            <li>{{ $t('quickSale.creationForm.templateNotes.topMargin') }}</li>
            <li>{{ $t('quickSale.creationForm.templateNotes.bottomMargin') }}</li>
            <li>{{ $t('quickSale.creationForm.templateNotes.leftMargin') }}</li>
            <li>{{ $t('quickSale.creationForm.templateNotes.rightMargin') }}</li>
          </ul>
        </li>
      </ul>
      <FormKitFileDropzone
        name="templateFile"
        validation="required|max:1"
        accept="application/pdf"
        label=""
      />
    </div>
    <FormKit
      name="includeVAT"
      type="checkbox"
      :label="$t('quickSale.creationForm.includeVAT.label')"
    />
    <QuickSalePdfLanguageSelector v-model="language" />

    <div class="flex gap-2 mt-2">
      <n-button
        type="primary"
        size="large"
        @click="handleSubmit(cloned)"
      >
        {{ $t('button.create') }}
      </n-button>

      <n-button
        type="primary"
        size="large"
        tertiary
        :loading="isLoadingPreview"
        @click="openPreview"
      >
        {{ $t('pdf.common.preview') }}
      </n-button>
    </div>
  </FormKit>
</template>
